"use client";
import React, {
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
import { PropertyType } from "src/types";
import { filterItems, ViewProps } from "../../design1/ListingPage1";
import { Design3Card } from "../components/Highlighted";
import { SearchBtn } from "./AnnouncementPage";
import { UnitsResponse } from "src/app/lib/useFetchUnits";
import { CardVariant3 } from "../../design2/components/CardVariant2";
import Select from "../../design2/components/Select3";
import { PropertyCardSkeleton } from "../../design2/components/Skeletons/Design2PropertyCard";
import { propertyData } from "../../design1/components/HeroSearchComponent";
import Button from "src/app/components/ui/Button";
import { Header, ResetIcon } from "../../design2/components/View";
import { TabType } from "../../design2/components/View";
import { getPriceRanges } from "../../design1/components/HeroSearchComponent";
import { statesData } from "src/app/components/constants/statesData";
import HeroPage3 from "../components/HeroPage3";
import { PropertySearchData } from "../../design1/components/HeroSearchComponent";
import StyledWraper from "../components/StyledWraper";
import CustomSelect from "../../design1/components/ui/form/CustomSelect";
import { List, Grid, options } from "../../design1/ListingPage1";
import { findPropertiesByType } from "src/app/components/constants/mockProperties";
import CardGrid from "src/app/components/CardGrid";
import HomeFilterSearch from "../components/HeroSearch";
import { DefaultProperty, NoNetwork } from "src/app/components/Icon";
import DefaultPageComponent from "src/app/components/ui/DefaultPageComponent";
import { useRouter, useSearchParams } from "next/navigation";
import {
  removeMany,
  removeUrlParam,
  usePropertyPurpose,
} from "../../design1/components/HeroSearch";
import useUnitsData from "src/app/components/hooks/useUnitInfo";
import NoSearchCriteria from "src/app/components/ui/NoSearchCriteria";
import { useUnits } from "src/app/hooks/useUnits";
import { useUnitFilterTabs } from "src/app/hooks/useUnitsTabs";
import useCustomEventListener from "src/app/hooks/useCustomEventListener";

export const PropertyPage3 = ({ listings }: { listings: PropertyType[] }) => {
  return (
    <PropertyView
      initialData={listings as unknown as UnitsResponse}
      header="Property Listing"
      type="rent"
      text={`Stay Your next move starts here-browse, find, and secure your perfect property today!"`}
      properties={[
        ...findPropertiesByType("rent"),
        ...findPropertiesByType("rent"),
        ...findPropertiesByType("rent"),
        ...findPropertiesByType("sale"),
        ...findPropertiesByType("sale"),
        ...findPropertiesByType("sale"),
        ...findPropertiesByType("short let"),
        ...findPropertiesByType("short let"),
        ...findPropertiesByType("short let"),
      ]}
    />
  );
};

export default PropertyPage3;

export interface PropertySearchFormRef {
  handleSearch: () => void;
}
export const PropertyView: React.FC<{
  properties: PropertyType[];
  initialData: UnitsResponse;
  type?: ViewProps;
  header: string;
  text: string;
  subText?: string;
  variant?: boolean;
  location?: string; // ✅ Add location prop
}> = ({ 
  properties, 
  header, 
  text, 
  initialData, 
  variant, 
  type, 
  subText,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const searchParams = useSearchParams();
  const searchFormRef = useRef<PropertySearchFormRef>(null);

  // ✅ Sync search term with URL on mount/update
  useEffect(() => {
    const value = searchParams.get("search") || "";

    setSearchTerm(value);
  }, [searchParams]);

  // ✅ Trigger search in child component via ref
  const triggerSearchFromParent = () => {
    if (searchFormRef.current) {
      searchFormRef.current.handleSearch();
    }
  };

  return (
    <div>
      <CustomizedHero
        func={triggerSearchFromParent}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        variant={variant}
        text={text}
        header={header}
      />

      <View2
        initialData={initialData}
        ref={searchFormRef}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        text={subText}
        type={type}
        properties={properties}
       
      />
    </div>
  );
};

export const CustomizedHero: React.FC<{
  header: string;
  text: string;
  variant?: boolean;
  func: () => void;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  onSearch?: () => void;
}> = ({ header, text, variant, searchTerm, func, setSearchTerm }) => {

  return (
    <HeroPage3 className="!pb-14">
      <div
        className={`flex items-start md:flex-col gap-4 pt-8 justify-between ${
          variant && "!pt-14"
        }`}
      >
        <div className="flex-col flex-1 flex gap-3">
          <h1 className={`${variant && "!mb-10"}`}>{header}</h1>
          {!variant ? (
            <HomeFilterSearch variant={true} />
          ) : (
            <Search
              func={func}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          )}
        </div>
        <h2 className="max-w-xl h5">{text}</h2>
      </div>
    </HeroPage3>
  );
};

interface View2Props {
  properties: PropertyType[];
  type?: ViewProps;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  text?: string;
  initialData: UnitsResponse;
}

export const View2 = forwardRef<PropertySearchFormRef, View2Props>(
  ({ properties, type, text, searchTerm, initialData }, ref) => {
    const {
      displayedCount,
      handleLoadMore,
      view,
      setView,
      hasRelevantParams,
      setSelectedValue,
      dataToMap,
      totalResults,
      loading,
      error,
      handleSelect,
      hasMore,
      isFetching,
      unitss,
      hydrated,
      selectedValue,
    } = useUnits({ 
      isDemo, 
      placeholder: initialData, 
      properties, 
      type,
       // ✅ Pass location to hook
    });

    // ✅ Better loading state
    const showSkeleton = loading && !isDemo && type === "rent";
    const isShowingAll = displayedCount >= totalResults;

    if (error && type && !isDemo) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
        console.log({errorMessage})

      return <NoNetwork />;
    }

    return (
      <div className="wrapper py-8">
        <div className="results-filter md:items-start sm:flex-col sm:gap-3 mb-5 items-center flex justify-between">
          <h3 className="h4">
            Showing {displayedCount} / {totalResults} results
          </h3>
          <div className="search-swithc items-center flex gap-3">
            <div className="select border border-themeColor px-1 py-[6px] rounded-lg flex items-center">
              <span>Sort by: </span>
              <CustomSelect
                options={options}
                value={selectedValue}
                onChange={setSelectedValue}
                handleSelect={handleSelect}
                placeholder="Default"
              />
            </div>
            <div className="switch">
              <ViewToggle setView={setView} view={view} />
            </div>
          </div>
        </div>

        <AdvancedSearch ref={ref} searchTerm={searchTerm} />

        <div className="py-10">
          {!loading && hydrated && !isDemo && dataToMap.length === 0 ? (
            hasRelevantParams ? (
              <NoSearchCriteria />
            ) : (
              <div className="text-center">
                <DefaultPageComponent
                  image="/images/not-found-optimize.gif"
                  text={
                    text ||
                    `We're currently updating our listings. Soon, you'll be able to explore a wide range of properties - whether you're looking to rent, buy, or invest. From residential homes to commercial spaces, we've got something for everyone. Check back shortly to discover your next space!`
                  }
                  svg={DefaultProperty}
                />
              </div>
            )
          ) : (
            <>
              {/* ✅ Property Grid */}
              <CardGrid
                gap={view === "list" ? 20 : 15}
                maxColumns={view === "list" ? 2 : 4}
              >
                {showSkeleton
                  ? Array.from({ length: 14 }).map((_, index) => (
                      <PropertyCardSkeleton
                        variant={view === "list" ? true : false}
                        key={index}
                      />
                    ))
                  : (hydrated ? dataToMap : unitss)?.map((item, index) =>
                      view === "grid" ? (
                        <Design3Card 
                          className="!w-full" 
                          key={item.id || index} 
                          {...item} 
                        />
                      ) : (
                        <CardVariant3 
                          className="!w-full" 
                          key={item.id || index} 
                          {...item} 
                        />
                      )
                    )}
              </CardGrid>

              {/* ✅ Loading spinner during pagination */}
              {isFetching && hydrated && dataToMap.length > 0 && !showSkeleton && (
                <div className="flex justify-center mt-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-themeColor"></div>
                </div>
              )}
            </>
          )}

          {/* ✅ Better Load More button conditions */}
          {hasMore && !isShowingAll && type === "rent" && hydrated && dataToMap.length > 0 && (
            <Button
              onClick={()=>{
                handleLoadMore()
              }}
              disabled={isFetching}
              className="w-max primary-btn block mt-14 !px-4 hover:bg-themeColor/80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity mx-auto"
            >
              {isFetching ? "Loading..." : "View More"}
            </Button>
          )}
        </div>
      </div>
    );
  }
);

View2.displayName = "View2";

// export const View2: React.FC<View2Props> = ({
//   properties,
//   type,
//   text,
//   searchTerm,
// }) => {};

const AdvancedSearch = forwardRef<
  PropertySearchFormRef,
  { searchTerm: string }
>(({ searchTerm }, ref) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const propertyPurpose = usePropertyPurpose();
  const GetFromUrl = (name: string): string | null => {
    console.log({name})

    return "";
  };
  const [unitData, setUnitData] = useState({
    unit_type: "",
    unit_sub_type: "",
  });

  // Step 2: Set values using useEffect
  useEffect(() => {
    const getFromUrl = (name: string): string => {
      return searchParams.get(name) || "";
    };
    setUnitData({
      unit_type: getFromUrl("unit_type"),
      unit_sub_type: getFromUrl("unit_sub_type"),
    });
  }, [searchParams]);
  const initialFormData = useMemo(
    () => ({
      search: searchTerm || "",
      propertyType: (propertyPurpose as "For rent") || "",
      category: GetFromUrl("category") || "",
      unit_type: unitData.unit_type || GetFromUrl("unit_type") || "",
      min_price: GetFromUrl("min_price") || "",
      max_price: GetFromUrl("max_price") || "",
      unit_sub_type:
        unitData.unit_sub_type || GetFromUrl("unit_sub_type") || "",
      priceRange: "",
      state: GetFromUrl("state") || "",
      local_government: GetFromUrl("local_government") || "",
    }),
    [searchParams, propertyPurpose, router, searchTerm]
  );
  const propertyTypes = ["For sale", "For rent", "For shortlet"];
  const [formData, setFormData] = useState<PropertySearchData>(initialFormData);
  const clearAllQueryParams = () => {
    const currentUrl = new URL(window.location.href);
    window.history.replaceState(null, "", currentUrl.pathname);
  };
  const handleReset = () => {
    setFormData(initialFormData);
    clearAllQueryParams();
  };
  const { minPrices, maxPrices } = getPriceRanges(formData.propertyType);
  const handleChange = (field: keyof PropertySearchData) => (value: string) => {
    if (!value || value === "") {
      removeUrlParam(field);
    }
    setFormData((prevData) => {
      let updatedData = { ...prevData, [field]: value };
      if (field === "propertyType" && prevData.propertyType !== value) {
        updatedData = {
          ...updatedData,
          category: "",
          unit_type: "",
          unit_sub_type: "",
        };
        removeMany(["category", "unit_type", "unit_sub_type"]);
      }
      if (field === "category" && prevData.category !== value) {
        updatedData = { ...updatedData, unit_type: "", unit_sub_type: "" };
        removeMany(["unit_sub_type", "unit_type"]);
      }
      if (field === "unit_type" && prevData.unit_type !== value) {
        updatedData = { ...updatedData, unit_sub_type: "" };
        removeUrlParam("unit_sub_type");
      }
      if (field === "state" && prevData.state !== value) {
        updatedData = {
          ...updatedData,
          local_government: "",
        };
        removeUrlParam("local_government");
      }
      return updatedData;
    });
  };
  const handleSearch = () => {
    const { category, min_price, max_price, propertyType, ...filters } =
      formData;
    const params = new URLSearchParams();
    // Convert price values
    const convertPrice = (price: string) => {
      if (!price || price === "No Min" || price === "No Max") return "";
      return price
        .replace("₦", "")
        .replace(" Million", "000000")
        .replace(" Billion", "000000000")
        .replace(/,/g, "");
    };
    if (min_price) params.set("min_price", convertPrice(min_price));
    if (max_price) params.set("max_price", convertPrice(max_price));
    if (category) params.set("category", category.toLowerCase());
    if (searchTerm) params.set("search", searchTerm.toLowerCase());
    // Clean propertyType
    const cleanPropertyType = (type: string) => {
      let cleaned = type
        .replace(/\bfor\b/gi, "")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase();

      if (cleaned === "short-let") cleaned = "shortlet";
      return cleaned;
    };
    // Append other filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
    // Build base path
    let basePath = "/properties";
    if (
      category &&
      ["rent", "sale", "shortlet"].includes(category.toLowerCase())
    ) {
      basePath += `/${category.toLowerCase()}`;
    }
    // Add propertyType as path if it exists
    // Add propertyType as path if it exists
    if (propertyType) {
      const cleanedType = cleanPropertyType(propertyType);
      basePath += `/${cleanedType}`;
    }

    const queryString = params.toString();
    const fullUrl = queryString ? `${basePath}?${queryString}` : basePath;

    // Compare current path (without query) to new path
    const currentPath = window.location.pathname;

    if (currentPath !== basePath) {
      // Path changed → use router.push (full navigation)
      router.push(fullUrl, { scroll: false });
    } else {
      // Path same → only query params changed → replaceState (instant URL update)
      window.history.replaceState(null, "", fullUrl);
    }

  
  };

  useCustomEventListener("my-custom-event", handleSearch);
  useImperativeHandle(ref, () => ({
    handleSearch,
  }));

  const res = useUnitsData(); // Hook to fetch unit data
  const states = res.unitsData?.cleanedAreas;
  // const areas = res.unitsData?.unitInfo;
  const statesToMap = isDemo ? statesData : states;
  const categories = Object.keys(propertyData);
  const stateNames = Array.isArray(statesToMap)
    ? statesToMap
        .map((state) => {
          const keys = Object.keys(state);
          return keys.length > 0 ? keys[0] : null;
        })
        .filter((key): key is string => key !== null)
    : [];

  // I am getting local governments here
  const selectedStateData = Array.isArray(statesToMap)
    ? statesToMap.find((state) =>
        Object.prototype.hasOwnProperty.call(state, formData.state)
      )
    : undefined;
  const localGovernments = selectedStateData
    ? Object.keys(
        selectedStateData[
          formData.state as keyof typeof selectedStateData
        ] as object
      )
    : [];

  return (
    <div className="flex-col  flex gap-6">
      <StyledWraper behindClassName="!overflow-visible">
        <div className="form bg-white xs:px-3  p-8 rounded-2xl">
          <div
            style={{
              boxShadow: "0px 2px 10px 0px #2121210D",
            }}
            className="flex bg-white items-end justify-between sm:flex-col md:gap-3 gap-8"
          >
            <div className="frist flex-1 flex-wrap flex xs:flex-col gap-8 md:gap-3">
              <Header design3 variant header="Property Type">
                <Select
                  cancelStyle="3"
                  inputParentStyle="fade"
                  placeholder="Property Type"
                  name="type"
                  value={formData.propertyType}
                  options={propertyTypes}
                  onChange={handleChange("propertyType")}
                />
                <Select
                  cancelStyle="3"
                  inputParentStyle="fade"
                  placeholder="Category"
                  name="subtype"
                  value={formData.category}
                  options={categories}
                  onChange={handleChange("category")}
                />
              </Header>
              <Header design3 variant header="Location">
                <Select
                  cancelStyle="3"
                  placeholder="Select State"
                  inputParentStyle="fade"
                  name="state"
                  value={formData.state}
                  options={stateNames}
                  onChange={(value: string) => {
                    handleChange("state")(value);
                    handleChange("local_government")("");
                  }}
                />
                <Select
                  cancelStyle="3"
                  inputParentStyle="fade"
                  placeholder="Select LGA"
                  value={formData.local_government}
                  name="lga"
                  options={localGovernments}
                  onChange={(value: string) => {
                    handleChange("local_government")(value);
                  }}
                />
              </Header>
              <Header design3 variant header="Price Range">
                <Select
                  cancelStyle="3"
                  dropdownStyle="!right-0"
                  inputParentStyle="fade"
                  placeholder="Minimum Price"
                  name="minPrice"
                  value={formData.min_price}
                  options={minPrices}
                  onChange={handleChange("min_price")}
                />
                <Select
                  inputParentStyle="fade"
                  cancelStyle="3"
                  placeholder="Maximum Price"
                  name="maxPrice"
                  value={formData.max_price}
                  options={maxPrices}
                  onChange={handleChange("max_price")}
                />
              </Header>
            </div>
            <div className="buttons max-w-[200px] sm:max-w-full w-full  h-max flex sm:flex-row flex-col items-end gap-2">
              <Button
                onClick={handleReset}
                className=" flex !h-max primary-btn fade sm:basis-[48%] justify-center !py-3  gap-2 items-center border !w-full border-themeColor !text-themeColor bg-white"
              >
                {" "}
                <ResetIcon /> Reset
              </Button>
              <Button
                onClick={handleSearch}
                className="!py-3 sm:px-1 sm:basis-[48%] sm:flex-1 whitespace-nowrap fade !h-max  w-full "
              >
                Find Property
              </Button>
            </div>
          </div>
        </div>
      </StyledWraper>

      <StyledWraper className="!h-max  bg-white">
        <Tabs />
      </StyledWraper>
    </div>
  );
});

AdvancedSearch.displayName = "AdvancedSearch";

// const AdvancedSearch = ({ searchTerm }: { searchTerm: string }) => {};

interface ViewToggleProps {
  view: "list" | "grid";
  setView: Dispatch<SetStateAction<"list" | "grid">>;
}
export const ViewToggle: React.FC<ViewToggleProps> = ({ view, setView }) => {
  return (
    <div className="flex gap-4">
      <button
        onClick={() => setView("grid")}
        className={`px-2 py-1 border border-themeColor rounded-lg ${
          view === "grid" ? "bg-themeColor text-white" : "bg-transparent"
        }`}
      >
        <Grid color={view === "grid" ? "white" : "var(--primary-color)"} />
      </button>
      <button
        onClick={() => setView("list")}
        className={`px-2 border rounded-lg border-themeColor py-1 ${
          view === "list" ? "bg-themeColor text-white" : "bg-transparent"
        }`}
      >
        <List color={view === "list" ? "white" : "var(--primary-color)"} />
      </button>
    </div>
  );
};

const Tabs: React.FC = () => {
  const {
    headerItems: headerToMap,
    current,
    currentSub,
    uniqueItems,
    handleHeaderClick,
    handleSubItemClick,
  } = useUnitFilterTabs(isDemo, filterItems);
  return (
    <div className="items   p-4">
      <div className="first  h-max mb-3 flex-wrap flex">
        {headerToMap.map((tab) => (
          <span
            key={tab}
            onClick={() => handleHeaderClick(tab as TabType)}
            className={`cursor-pointer whitespace-nowrap h-max  px-2 ${
              tab.toLowerCase() === current.toLowerCase()
                ? "text-themeColor bg-[rgba(var(--primary-color-rgb),0.1)] font-semibold"
                : ""
            }`}
          >
            {tab}
          </span>
        ))}
      </div>
      <div className="second flex flex-wrap">
        {current === "All"
          ? ""
          : ["all", ...uniqueItems, "others"].map((item, index) => {
              return (
                <span
                  onClick={() => handleSubItemClick(item)}
                  className={`capitalize ${
                    item === "others" && "hidden"
                  } cursor-pointer hover:text-themeColor ${
                    currentSub?.toLowerCase() === item.toLowerCase() &&
                    "text-themeColor"
                  } px-2 text-sm`}
                  key={index}
                >
                  {item}
                </span>
              );
            })}
        {}
      </div>
    </div>
  );
};

const Search: React.FC<{
  searchTerm: string;
  func: () => void;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}> = ({ setSearchTerm, searchTerm, func }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Always update ?search=... in URL instantly
    const params = new URLSearchParams(window.location.search);
    if (value.trim()) {
      params.set("search", value.trim());
    } else {
      params.delete("search"); // remove param if empty
    }
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params.toString()}`
    );
  };

  return (
    <div className="w-full min-w-[400px] sm:min-w-full text-white">
      <div className="bg-white rounded-xl w-full max-w-[450px] items-center p-1 pl-3 flex justify-between">
        <div className="flex w-full gap-1 basis-full flex-1 items-center">
          <input
            value={searchTerm}
            onChange={handleChange}
            placeholder="Search by ID or Title"
            className="outline-none block flex-1 w-full text-base placeholder:text-disabled-main focus:outline-none text-primary border-none"
            type="text"
          />
        </div>
        <div onClick={func} className="cursor-pointer">
          <SearchBtn />
        </div>
      </div>
    </div>
  );
};
